import { TimeSlot } from './TimeSlot';

/**
 * シフトステータス（UI表示用・旧互換）
 * @description シフト希望の承認状態を表す
 */
export type ShiftStatus =
  | 'pending'    // 保留中（管理者による確認待ち）
  | 'confirmed'  // 確定済み（削除不可）
  | 'cancelled'; // キャンセル済み

/**
 * スロットタイプ（API仕様）
 * @description シフトの種別を表す
 */
export type SlotType = 'normal' | 'urgent';

/**
 * シフトエンティティ（API仕様準拠）
 * @description バックエンドAPIとの通信で使用するシフトデータ構造
 * @note 1レコード = 1時間帯
 */
export interface Shift {
  /** シフトID */
  id: number;
  /** 医師ID */
  doctor_id: number;
  /** シフト対象日（YYYY-MM-DD形式） */
  shift_date: string;
  /** 開始時刻（HH:mm:ss形式） */
  start_time: string;
  /** 終了時刻（HH:mm:ss形式） */
  end_time: string;
  /** スロット種別 */
  slot_type: SlotType;
  /** 有効フラグ */
  is_active: boolean;
  /** シフト作成元 (manual/rule/csv/my_shift) */
  source: string;
  /** 固定シフト確定済みフラグ */
  is_fixed_confirmed: boolean;
}

/**
 * シフト作成・更新リクエスト用の型
 * @description APIリクエスト時に使用（idなし）
 */
export interface ShiftInput {
  /** シフト対象日（YYYY-MM-DD形式） */
  shift_date: string;
  /** 開始時刻（HH:mm形式） */
  start_time: string;
  /** 終了時刻（HH:mm形式） */
  end_time: string;
}

/**
 * シフト希望エンティティ（UI表示用・旧互換）
 * @description 医師が提出した1日分のシフト希望データ（フロントエンド内部用）
 */
export interface MyShift {
  /** シフトID（サーバー側で自動採番） */
  id: number;
  /** シフト対象日（YYYY-MM-DD形式） */
  date: string;
  /** タイムスロット配列（1時間単位、最大13スロット） */
  timeSlots: TimeSlot[];
  /** シフトステータス */
  status: ShiftStatus;
  /** 作成日時（ISO 8601形式） */
  createdAt: string;
  /** 更新日時（ISO 8601形式） */
  updatedAt: string;
}

/**
 * 月次ステータスとシフト作成元から、スロットが編集不可かを判定する
 *
 * | monthly_status    | source=rule | source!=rule |
 * |-------------------|-------------|--------------|
 * | fixed_recruiting  | 編集可      | 編集不可     |
 * | fixed_confirmed   | 編集不可    | 編集不可     |
 * | recruiting        | 編集不可    | 編集可       |
 * | closed / confirmed| 編集不可    | 編集不可     |
 * | draft / null      | 編集可      | 編集可       |
 */
const computeIsConfirmed = (source: string, monthlyStatus: string | null): boolean => {
  switch (monthlyStatus) {
    case 'fixed_recruiting':
      return source !== 'rule';
    case 'fixed_confirmed':
    case 'confirmed':
    case 'closed':
      return true;
    case 'recruiting':
      return source === 'rule';
    default:
      return false;
  }
};

/**
 * APIレスポンスのShift配列をUI用のMyShift形式に変換
 * @param shifts - APIから取得したシフト配列
 * @param monthlyStatus - 月次シフト枠ステータス
 * @returns UI表示用のMyShift配列（日付でグループ化）
 */
export const convertShiftsToMyShifts = (shifts: Shift[], monthlyStatus: string | null = null): MyShift[] => {
  // 日付でグループ化
  const groupedByDate = shifts.reduce((acc, shift) => {
    const date = shift.shift_date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(shift);
    return acc;
  }, {} as Record<string, Shift[]>);

  // MyShift形式に変換
  return Object.entries(groupedByDate).map(([date, dateShifts]) => {
    const timeSlots: TimeSlot[] = dateShifts.map((shift, index) => ({
      id: shift.id || index,
      startTime: shift.start_time.substring(0, 5), // HH:mm:ss → HH:mm
      endTime: shift.end_time.substring(0, 5),     // HH:mm:ss → HH:mm
      slotType: shift.slot_type ?? 'normal',
      isConfirmed: computeIsConfirmed(shift.source ?? 'my_shift', monthlyStatus),
      source: shift.source ?? 'my_shift',
    }));

    const hasConfirmedSlot = timeSlots.some(s => s.isConfirmed);
    const allCancelled = dateShifts.every(s => !s.is_active);

    return {
      id: dateShifts[0]?.id || 0,
      date,
      timeSlots,
      status: allCancelled ? 'cancelled' : hasConfirmedSlot ? 'confirmed' : 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  });
};

